// [PURPOSE]: Pure helper — compute condition consequences directly from
//   playerStats.conditions, bypassing the effectRegistry intermediate.
//
// [WHY]: effectRegistry is populated by CharacterContext useEffect AFTER the
//   first render. UI components that query effectCalculator.hasAutoFailSave /
//   hasAttacksAgainst / hasAutoCritWithin5ft return false on initial paint
//   because the registry hasn't been populated yet. This causes condition-
//   consequence banners (StatsPanel, VitalStatsCard, CombatTab) to be
//   invisible on first paint when a paralyzed/stunned/etc. character is loaded.
//
//   The fix: read playerStats.conditions directly + walk SRD_CONDITIONS.numericEffects
//   synchronously. The SRD_CONDITIONS import is module-load-time; no async,
//   no registry, no timing race.
//
// [SYSTEM-AGNOSTIC NOTE]: The numericEffects shape is system-agnostic. PF2e /
//   Mouseguard / CoC conditions can register the same numericEffects contract
//   and this helper will aggregate them transparently. When Sub-plan 3B ships
//   a Condition Creator, extending this util to union SRD + custom is a 5-line
//   change (union the two catalogs before the loop).
//
// [REFERENCES]:
//   - Sub-plan 1Q-F-7d-followup: inline-read fix for effectRegistry timing race
//   - Sub-plan 1Q-F-7b-ii (2026-05-28): adds autoFailChecks + disadvantageCheckAbilities
//     for ability-check consequences (poisoned/frightened disadvantage + paralyzed/stunned/
//     petrified STR+DEX auto-fail + unconscious all-abilities auto-fail).
//   - SRD_CONDITIONS: src/data/srdConditions.js
//   - Consumers: StatsPanel.js, VitalStatsCard.js, CombatTab.js
//   - effectCalculator.hasAutoFailSave / hasAttacksAgainst / hasAutoCritWithin5ft
//     remain AS-IS for non-UI consumers (QuickActionsBar L4526, save-roll modal).
//
// [RULE 22]: This is the production function imported by both UI components
//   and tests. No inline replica in any consumer.
// [RULE 31(f)]: producer (playerStats.conditions) → reader-query
//   (computeConditionConsequences) → assert (autoFailAbilities / hasAdvAtk /
//   hasAutoCrit). Test: sub-plan-1Q-F-7d-followup-inline-reader-runtime.test.js

import { SRD_CONDITIONS } from '../data/srdConditions';

/**
 * Compute condition consequences directly from playerStats.conditions.
 *
 * @param {object} playerStats - The active character state object.
 *   May be null/undefined (returns empty result).
 * @returns {{
 *   autoFailAbilities: Set<string>,
 *   autoFailChecks: Set<string>,
 *   disadvantageCheckAbilities: Set<string>,
 *   hasAdvAtk: boolean,
 *   hasAutoCrit: boolean,
 *   hasDisadvantageOnDexSave: boolean,
 *   activeConditionNames: string[],
 * }}
 *
 * Handles both string conditions ('paralyzed') and object-shape conditions
 * ({ name: 'Paralyzed' }) in playerStats.conditions.
 */
export function computeConditionConsequences(playerStats) {
  const result = {
    autoFailAbilities: new Set(),
    // [Sub-plan 1Q-F-7b-ii 2026-05-28]: NEW — ability-check auto-fail Set.
    // Populated from type:'check' + operation:'autoFail' rows in numericEffects.
    // Covers: Paralyzed/Stunned/Petrified (STR+DEX) + Unconscious (all 6).
    autoFailChecks: new Set(),
    // [Sub-plan 1Q-F-7b-ii 2026-05-28]: NEW — ability-check disadvantage Set.
    // Populated from type:'check' + operation:'disadvantage' rows.
    // Covers: Poisoned + Frightened (all 6 via 'all'-scope row).
    disadvantageCheckAbilities: new Set(),
    hasAdvAtk: false,
    hasAutoCrit: false,
    hasDisadvantageOnDexSave: false,
    activeConditionNames: [],
  };

  const raw = playerStats?.conditions;
  if (!Array.isArray(raw) || raw.length === 0) return result;

  for (const entry of raw) {
    // Handle both string ('paralyzed') and object ({ name: 'Paralyzed' }) shapes
    const name = typeof entry === 'string' ? entry : (entry?.name || '');
    if (!name) continue;

    const key = name.toLowerCase();
    const cond = SRD_CONDITIONS[key];
    if (!cond?.numericEffects) continue;

    result.activeConditionNames.push(cond.name);

    for (const eff of cond.numericEffects) {
      if (eff.type === 'save' && eff.operation === 'autoFail' && eff.target) {
        result.autoFailAbilities.add(eff.target.toLowerCase());
      }
      if (eff.type === 'attacksAgainst' && eff.operation === 'advantage') {
        result.hasAdvAtk = true;
      }
      if (eff.type === 'attacksAgainstCrit' && eff.operation === 'autoSuccess') {
        result.hasAutoCrit = true;
      }
      if (
        eff.type === 'save' &&
        eff.operation === 'disadvantage' &&
        eff.target &&
        eff.target.toLowerCase() === 'dexterity'
      ) {
        result.hasDisadvantageOnDexSave = true;
      }
      // [Sub-plan 1Q-F-7b-ii 2026-05-28]: NEW — ability-check auto-fail.
      // Covers: Paralyzed/Stunned/Petrified (target:'strength'/'dexterity')
      //         + Unconscious (target:'all' → all 6 abilities).
      if (eff.type === 'check' && eff.operation === 'autoFail' && eff.target) {
        const t = eff.target.toLowerCase();
        if (t === 'all') {
          ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma']
            .forEach(a => result.autoFailChecks.add(a));
        } else {
          result.autoFailChecks.add(t);
        }
      }
      // [Sub-plan 1Q-F-7b-ii 2026-05-28]: NEW — ability-check disadvantage.
      // Covers: Poisoned + Frightened (target:'all' → all 6 abilities).
      if (eff.type === 'check' && eff.operation === 'disadvantage' && eff.target) {
        const t = eff.target.toLowerCase();
        if (t === 'all') {
          ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma']
            .forEach(a => result.disadvantageCheckAbilities.add(a));
        } else {
          result.disadvantageCheckAbilities.add(t);
        }
      }
    }
  }

  return result;
}
